// Identity: anonymous-first, claimed at sign-up (docs/ARCHITECTURE.md).

import { create } from "zustand";
import { api } from "@/lib/api";
import { anonymousId, identifyAccount, resetIdentity, track } from "@/analytics/service";
import { startSync, stopSync } from "@/graph/sync";

export interface Profile {
  id: string;
  email: string;
  name: string | null;
  handle: string | null;
  avatar: string | null;
  theme: string | null;
  shortcuts: string | null;
}

interface AuthState {
  user: Profile | null;
  checked: boolean;
  refresh: () => Promise<void>;
  signup: (email: string, password: string, name?: string) => Promise<void>;
  signin: (email: string, password: string) => Promise<void>;
  magic: (email: string) => Promise<"email" | "dev-inbox">;
  signout: () => Promise<void>;
}

// OAuth + magic-link sign-ins complete on the worker, which redirects back with
// ?via=magic|google&new=0|1. Emit the auth.signin/auth.signup event here (once
// identity is set), matching the password flow, then strip the transient params.
function trackRedirectSignIn(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const via = params.get("via");
  if (via !== "magic" && via !== "google") return;
  if (params.get("new") === "1") track("auth.signup", { via, claimedObjects: 0 });
  else track("auth.signin", { via });
  params.delete("via");
  params.delete("new");
  params.delete("welcome");
  const qs = params.toString();
  window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  checked: false,

  async refresh() {
    try {
      const { user } = await api<{ user: Profile | null }>("/api/auth/me");
      set({ user, checked: true });
      if (user) {
        identifyAccount(user.id);
        startSync();
        trackRedirectSignIn();
      }
    } catch {
      set({ checked: true }); // offline is a first-class state
    }
  },

  async signup(email, password, name) {
    const res = await api<{ id: string; claimed: number }>("/api/auth/signup", {
      method: "POST",
      json: { email, password, name, anonId: anonymousId() },
    });
    track("auth.signup", { via: "password", claimedObjects: res.claimed });
    await useAuth.getState().refresh();
  },

  async signin(email, password) {
    await api("/api/auth/signin", { method: "POST", json: { email, password } });
    track("auth.signin", { via: "password" });
    await useAuth.getState().refresh();
    // Returning creators may carry anonymous work made while signed out.
    void api("/api/auth/claim", { method: "POST", json: { anonId: anonymousId() } }).catch(() => undefined);
  },

  async magic(email) {
    const res = await api<{ delivered: "email" | "dev-inbox" }>("/api/auth/magic", { method: "POST", json: { email } });
    return res.delivered;
  },

  async signout() {
    await api("/api/auth/signout", { method: "POST" });
    resetIdentity();
    stopSync();
    set({ user: null });
  },
}));
